import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import SelectTipoCarga from "../../../components/UI/SelectInput/SelectTipoCargaFormik";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import CheckBoxFormik from "../../../components/UI/Checkbox/Checkbox";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import { AuthContext } from "../../../context/AuthContext";

const validationSchema = Yup.object().shape({
  sqProcedimento: Yup.string().default(""),
  nomeProcedimento: Yup.string()
    .ensure()
    .default("")
    .required("Informe o nome")
    .min(1, "Nome muito curto")
    .max(500, "Nome muito longo"),
  descricao: Yup.string()
    .ensure()
    .default("")
    .required("Informe a descricao")
    .min(1, "Descricao muito curta")
    .max(500, "Descricao muita longa"),
  sqTipoCarga: Yup.string().default("").required("Informe tipo de Carga"),
  stProcedimento: Yup.boolean().default(true),
  sefisc: Yup.boolean(),
  diligencia: Yup.boolean(),
  audEspecifica: Yup.boolean(),
  monitoramento: Yup.boolean(),
});

const initialValues = {};

validationSchema._nodes.forEach((key) => {
  if (key === "diligencia" || key === "audEspecifica" || key === "monitoramento" || key === "sefisc") {
    initialValues[key] = false;
  } else {
    initialValues[key] = "";
  }
});

const FormProcedimento = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sqProcedimento) requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraProcedimento, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.incluiProcedimento, values);
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Procedimento salvo com sucesso");
      history.push("/app/procedimento");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const {
    request: procedimentoRequest,
    data: procedimentoData,
    errors: procedimentoErrors,
    isLoading: procedimentoIsLoading,
  } = useApiClient();

  useEffect(() => {
    sessionStorage.setItem("back", true);
    if (state && state.itemKey) {
      procedimentoRequest(apiRoutes.fiscalizacao.parametrizacao.consultaProcedimento, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [procedimentoRequest, state]);

  useEffect(() => {
    if ((!procedimentoData && !procedimentoErrors) || procedimentoIsLoading) return;

    if (!formik.values.sqProcedimento && procedimentoData && procedimentoData.result.length) {
      const info = {
        codigo: procedimentoData?.result[0]?.sqProcedimento,
        data: {
          documentoInclusao: procedimentoData?.result[0]?.documentoInclusao,
          nomeFormalInclusao: procedimentoData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: procedimentoData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: procedimentoData?.result[0]?.documentoAlteracao,
          dataInclusao: procedimentoData?.result[0]?.dataInclusao,
          dataAlteracao: procedimentoData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);
      formik.setValues({ ...procedimentoData.result[0] });
    }

    if (procedimentoErrors) {
      toast.error(procedimentoErrors.message);
    }
  }, [formik, procedimentoData, procedimentoErrors, procedimentoIsLoading]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Procedimentos</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sqProcedimento ? "Alteração do Procedimento" : "Inclusão do Procedimento"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sqProcedimento" />
                </Col>
              </Row>
              {/* <Button href="#" className="btn btn-primary btn-sm btn-user">Botão</Button> */}
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={dataForm}
                errors={errorForm}
                isLoading={isLoading || procedimentoIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sqProcedimento
                      ? !isAllowed(funcionalidades.procedimentoFiscal.alterar)
                      : !isAllowed(funcionalidades.procedimentoFiscal.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="nomeProcedimento" maxLength="100" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12}>
                      <Form.Label>Descrição</Form.Label>
                      <TextInputFormik formik={formik} field="descricao" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Carga</Form.Label>
                      <SelectTipoCarga formik={formik} field="sqTipoCarga" />
                    </Form.Group>
                    <Form.Group as={Col} sm={20} md={6}>
                      <Form.Label>Tipos de Ação Fiscal</Form.Label>
                      <br />
                      <CheckBoxFormik inline="inline" formik={formik} field="diligencia" label="Diligência" />
                      <CheckBoxFormik
                        inline="inline"
                        formik={formik}
                        field="audEspecifica"
                        label="Auditoria Específica"
                      />
                      <CheckBoxFormik inline="inline" formik={formik} field="monitoramento" label="Monitoramento" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <CheckBoxFormik inline="inline" formik={formik} field="sefisc" label="SEFISC" />
                    </Form.Group>
                    {formik.values.sqProcedimento ? (
                      <Form.Group as={Col} sm={12} md={3}>
                        <CheckBoxFormik formik={formik} field="stProcedimento" label="Ativo" />
                      </Form.Group>
                    ) : null}
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Procedimento..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sqProcedimento
                            ? funcionalidades.procedimentoFiscal.alterar
                            : funcionalidades.procedimentoFiscal.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sqProcedimento && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormProcedimento;
